import React, { useMemo } from 'react';
import { Paperclip, FileText, Folder, Kanban } from 'lucide-react';
import { setShareAcceptToken, setSharedRequestOpen } from '../../store/uiModalStore';
import { EncryptedImage, EncryptedFileChip } from '../EncryptedAttachment';
import { LightboxImage } from '../ImageLightbox';
import ShareMessageCard from '../ShareMessageCard';
import TaskShareCard from '../TaskShareCard';
import { isTaskShare } from '../../utils/taskChatShare';
import { isAppointmentShare } from '../../utils/appointmentChatShare';
import AppointmentShareCard from '../AppointmentShareCard';
import VaultShareCard from '../VaultShareCard';
import { assetUrl } from './chatMessageUtils';
import { firstHref, linkifyText } from './chatLinks';
import { LinkPreview } from './LinkPreview';
import { CodeBlock } from './ChatCodeBlock';

// Display box for an image attachment whose intrinsic dimensions were stored
// at send time (metadata.w/h): the exact size CSS would settle on under the
// 320x240 max constraints, computable BEFORE any bytes arrive — so image rows
// occupy their final height from the first paint and never reflow the list.
export function attachBox(meta) {
  const w = Number(meta?.w);
  const h = Number(meta?.h);
  if (!w || !h || w < 0 || h < 0) return null;
  const scale = Math.min(320 / w, 240 / h, 1);
  return { width: Math.round(w * scale), height: Math.round(h * scale) };
}

export function MessageContent({ message: m, channelId, onOpenReference }) {
  // Hooks must run unconditionally before the early returns below — every
  // branch that needs linked/firstLinkHref reads from these, so the regex
  // scan only re-runs when the message body actually changes, not on
  // unrelated row state (pickerOpen, pendingEmojis, ...).
  const linked = useMemo(() => linkifyText(m.body), [m.body]);
  const firstLinkHref = useMemo(() => firstHref(m.body), [m.body]);

  if (m.kind === 'code') {
    return <CodeBlock code={m.body} language={m.metadata?.language} />;
  }
  if (m.kind === 'image') {
    const encrypted = Boolean(m.metadata?.enc);
    const url = encrypted ? m.metadata?.url : assetUrl(m.metadata?.url);
    const box = attachBox(m.metadata);
    return (
      <div className="chat-attach-wrap">
        {encrypted ? (
          <EncryptedImage
            channelId={m.channel_id || channelId}
            url={url}
            mime={m.metadata?.mime}
            name={m.metadata?.name}
            box={box}
          />
        ) : (
          <LightboxImage
            className="chat-attach-img"
            src={url}
            alt={m.metadata?.name || 'image'}
            style={box || undefined}
          />
        )}
        {m.body && <div className="chat-msg-text chat-attach-caption">{linked}</div>}
      </div>
    );
  }
  if (m.kind === 'file') {
    const encrypted = Boolean(m.metadata?.enc);
    return (
      <div className="chat-attach-wrap">
        {encrypted ? (
          <EncryptedFileChip
            channelId={m.channel_id || channelId}
            url={m.metadata?.url}
            mime={m.metadata?.mime}
            name={m.metadata?.name}
          />
        ) : (
          <a className="chat-file-chip" href={assetUrl(m.metadata?.url)} target="_blank" rel="noreferrer">
            <Paperclip size={14} />
            {m.metadata?.name || 'attachment'}
          </a>
        )}
        {m.body && <div className="chat-msg-text chat-attach-caption">{linked}</div>}
      </div>
    );
  }
  if (m.kind === 'share') {
    // Appointment cards carry display fields + appointment_uid, no token —
    // the viewer's own state comes from their invitation rows (PLAN §3.4).
    if (isAppointmentShare(m.metadata)) return <AppointmentShareCard metadata={m.metadata} />;
    // A task share carries ids only and is resolved against the reader's own
    // synced board — no token, no title in the message. It is checked before
    // the token-backed cards because it has no token to offer them.
    if (isTaskShare(m.metadata)) return <TaskShareCard metadata={m.metadata} />;
    // Recipient-scoped share card. Save → fork prompt; Play on a request →
    // open it straight into the builder as an unsaved tab.
    return (
      <ShareMessageCard
        metadata={m.metadata}
        onOpen={(meta) => {
          // `channelId` travels with the token because an ENCRYPTED snapshot
          // can only be opened with this channel's key, and neither
          // `GET /share/:token` nor `POST /share/:token/data` says which
          // channel a link is scoped to. `enc` lets the accept modal skip a
          // speculative data fetch on the (still normal) plaintext shares.
          if (meta?.token) {
            setShareAcceptToken({ token: meta.token, channelId, enc: meta.enc || null });
          }
        }}
        onPlay={(meta) => {
          if (meta?.token) {
            setSharedRequestOpen({
              token: meta.token,
              label: meta.label,
              channelId,
              enc: meta.enc || null,
            });
          }
        }}
      />
    );
  }
  if (m.kind === 'secret') {
    // E2E-encrypted vault share — decrypts in place, plaintext stays inside
    // the card's component state (never in chatStore).
    return (
      // `channelId` is the channel we are RENDERING in, deliberately not
      // `m.channel_id`: the AAD binding is only worth anything if the client
      // supplies its own idea of the context rather than the server's.
      <VaultShareCard
        metadata={m.metadata}
        senderName={m.sender?.name}
        senderUserId={m.sender_id}
        channelId={channelId}
        messageId={m.id}
      />
    );
  }
  if (m.kind === 'ref') {
    // Legacy reference chip — kept so old messages render unchanged.
    const ref = m.metadata || {};
    const Icon = ref.type === 'collection' ? Folder : ref.type === 'board' ? Kanban : FileText;
    return (
      <button className="chat-ref-chip" onClick={() => onOpenReference(ref)}>
        <Icon size={14} />
        {ref.label || ref.type || 'reference'}
      </button>
    );
  }
  return (
    <div className="chat-msg-text">
      {linked}
      <LinkPreview url={firstLinkHref} />
    </div>
  );
}
